import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { API_BASE } from '../services/api';
import './OrderSuccess.css';

function OrderSuccess() {
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get('order_id');
  const email = searchParams.get('email');
  const [orderStatus, setOrderStatus] = useState(null);
  const [loading, setLoading] = useState(Boolean(orderId));
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!orderId) {
      setLoading(false);
      return;
    }

    const fetchOrder = () => {
      setLoading(true);
      setError(null);
      const emailQs = email ? `?email=${encodeURIComponent(email)}` : '';
      fetch(`${API_BASE}/api/orders/${orderId}${emailQs}`)
        .then((res) => res.json())
        .then((data) => {
          if (data.success) {
            setOrderStatus(data.order);
          } else {
            setError(data?.error || 'Failed to load order status.');
          }
        })
        .catch((err) => {
          console.error('Failed to fetch order status:', err);
          setError('Failed to load order status.');
        })
        .finally(() => {
          setLoading(false);
        });
    };

    fetchOrder();

    // Poll for updates every 10 seconds
    const interval = setInterval(fetchOrder, 10000);
    return () => clearInterval(interval);
  }, [orderId, email]);

  let fulfillmentInfo = null;
  if (orderStatus && orderStatus.fulfillment_notes) {
    try {
      fulfillmentInfo = JSON.parse(orderStatus.fulfillment_notes);
    } catch (e) {
      fulfillmentInfo = null;
    }
  }

  const isPaymentConfirmed = (orderStatus?.payment_status || '').toLowerCase() === 'confirmed';
  const successTitle = isPaymentConfirmed ? 'Payment Confirmed' : 'Order Received';
  const successSubtitle = isPaymentConfirmed
    ? 'We received your payment. Provisioning starts next.'
    : 'We received your order. Payment confirmation is required before provisioning starts.';

  return (
    <div className="order-success-page">
      <div className="success-container">
        <div className="success-icon" aria-hidden="true"><i className="fa-solid fa-circle-check" /></div>
        <h1>{successTitle}</h1>
        <p>{successSubtitle}</p>

        {loading && (
          <div className="loading" aria-live="polite">Loading order details...</div>
        )}

        {error && (
          <div className="error" role="alert">{error}</div>
        )}

        {orderStatus && (
          <div className="order-details">
            <h2>Order Details</h2>
            <div className="detail-item">
              <span>Order ID:</span>
              <strong>{orderStatus.order_id}</strong>
            </div>
            <div className="detail-item">
              <span>Domain:</span>
              <strong>{orderStatus.domain}</strong>
            </div>
            <div className="detail-item">
              <span>Status:</span>
              <strong>{orderStatus.payment_status || 'pending'}</strong>
            </div>
          </div>
        )}

        <div className="next-steps">
          <h2>What Happens Next?</h2>
          {orderStatus?.fulfillment_status === 'blocked' ? (
            <>
              <p>
                We received your payment, but the domain became unavailable before we could register it.
              </p>
              <p>
                We'll contact you with alternatives or process a refund.
              </p>
              {fulfillmentInfo?.alternatives?.length ? (
                <div style={{ marginTop: 12 }}>
                  <h3 style={{ marginBottom: 8 }}>Suggested alternatives</h3>
                  <ul>
                    {fulfillmentInfo.alternatives.map((d) => (
                      <li key={d}>{d}</li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </>
          ) : (
            <ul>
                <li><i className="fa-solid fa-circle-arrow-right" aria-hidden="true" /> We’ll start provisioning your domain right after payment</li>
                <li><i className="fa-solid fa-circle-arrow-right" aria-hidden="true" /> You’ll receive an email update with next steps</li>
                <li><i className="fa-solid fa-circle-arrow-right" aria-hidden="true" /> You’ll receive a confirmation when it’s complete</li>
            </ul>
          )}
        </div>

        <div className="success-actions">
          {orderId && (
            <Link
              to={`/order/${orderId}/status${email ? `?email=${encodeURIComponent(email)}` : ''}`}
              className="btn btn-secondary"
              style={{ marginRight: '0.75rem' }}
            >
              View Order Status
            </Link>
          )}
          <Link to="/" className="btn btn-primary">
            Return Home
          </Link>
        </div>
      </div>
    </div>
  );
}

export default OrderSuccess;

